/**
 * LoginGate
 *
 * Wraps the authenticated app. If the user is not signed in it renders a
 * minimal Sign In form (no public registration — accounts are created by
 * owners via the Users page and delivered to users as an invite URL).
 *
 * Session is a server-issued httpOnly cookie. We never store credentials
 * in JS-accessible storage (no more sessionStorage / Basic Auth).
 *
 * If the URL is `/invite/<token>`, we render the AcceptInvite flow instead
 * of the sign-in form so new users can set their username + password.
 */

import { useEffect, useState, createContext, useContext, type ReactNode } from 'react';
import { apiFetch, getCsrfToken } from '../lib/api';
import logoUrl from '../assets/skr-logo.svg';

interface AuthUser {
  id: number;
  username: string;
  role: string;
  full_name: string | null;
  team_id: number | null;
}

interface AuthContextValue {
  logout: () => void;
  user: AuthUser | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  return useContext(AuthContext)!;
}

/** Detect `/invite/<token>` in location (works with wouter). */
function getInviteToken(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/invite\/([A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

export function LoginGate({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<'loading' | 'signed-out' | 'signed-in'>('loading');
  const [inviteToken, setInviteToken] = useState<string | null>(() => getInviteToken());

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  // Invite-acceptance form state
  const [inviteUsername, setInviteUsername] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [submittingInvite, setSubmittingInvite] = useState(false);

  // On mount (and whenever we think we're authed), try to load the current user.
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/auth/me')
      .then(r => r.ok ? r.json() : null)
      .then(me => {
        if (cancelled) return;
        if (me && typeof me.id === 'number') {
          setUser(me);
          setStatus('signed-in');
        } else {
          setStatus('signed-out');
        }
      })
      .catch(() => { if (!cancelled) setStatus('signed-out'); });
    return () => { cancelled = true; };
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!username || !password) return;
    setChecking(true);
    setError('');
    try {
      // Fetch a CSRF token first (server sets a companion cookie).
      await getCsrfToken();
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        let msg = 'Invalid username or password';
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch { /* ignore */ }
        setError(msg);
        return;
      }
      const me = await res.json();
      setUser(me);
      setStatus('signed-in');
    } catch {
      setError('Cannot connect to server');
    } finally {
      setChecking(false);
    }
  }

  async function handleAcceptInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteToken || !inviteUsername || !invitePassword) return;
    setSubmittingInvite(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      await getCsrfToken();
      const res = await apiFetch('/api/auth/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: inviteToken,
          username: inviteUsername,
          password: invitePassword,
          full_name: inviteFullName,
        }),
      });
      if (!res.ok) {
        let msg = 'Invite could not be accepted';
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch { /* ignore */ }
        setInviteError(msg);
        return;
      }
      setInviteSuccess('Account created. Redirecting to sign in…');
      // Drop the /invite/<token> URL and fall through to sign-in.
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', '/');
          setInviteToken(null);
        }
      }, 1200);
    } catch {
      setInviteError('Cannot connect to server');
    } finally {
      setSubmittingInvite(false);
    }
  }

  async function logout() {
    try {
      await getCsrfToken();
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch { /* ignore */ }
    setUser(null);
    setStatus('signed-out');
    setUsername('');
    setPassword('');
  }

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        Loading…
      </div>
    );
  }

  if (status === 'signed-out') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 10, padding: '36px 32px 32px', width: '100%', maxWidth: 380, boxShadow: '0 4px 6px -1px rgba(0,0,0,.07)' }}>
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <img src={logoUrl} alt="SKR Law" style={{ width: '100%', maxWidth: 300, height: 'auto', display: 'block', margin: '0 auto 14px' }} />
            <h1 style={{ fontSize: 19, fontWeight: 700, color: 'var(--text)' }}>
              {inviteToken ? 'Accept Invitation' : 'Sign In'}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Case Management System</p>
          </div>

          {inviteToken ? (
            <form onSubmit={handleAcceptInvite} noValidate>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Full Name</label>
                <input className="form-input" type="text" value={inviteFullName} onChange={e => setInviteFullName(e.target.value)} autoFocus placeholder="Your name" />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Choose Username</label>
                <input className="form-input" type="text" value={inviteUsername} onChange={e => setInviteUsername(e.target.value)} autoComplete="username" placeholder="e.g. jane.smith" />
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Choose Password</label>
                <input className="form-input" type="password" value={invitePassword} onChange={e => setInvitePassword(e.target.value)} autoComplete="new-password" placeholder="12+ chars, upper + lower + digit" />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Must be 12 characters or longer and include upper case, lower case, and a digit.
                </div>
              </div>
              {inviteError && <div style={{ background: 'var(--danger-light)', color: 'var(--danger)', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontWeight: 500 }}>{inviteError}</div>}
              {inviteSuccess && <div style={{ background: 'var(--success-light)', color: 'var(--success)', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontWeight: 500 }}>{inviteSuccess}</div>}
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '9px 14px' }} disabled={submittingInvite || !inviteUsername || !invitePassword}>
                {submittingInvite ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} noValidate>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Username</label>
                <input className="form-input" type="text" value={username} onChange={e => setUsername(e.target.value)} autoFocus autoComplete="username" placeholder="Enter username" />
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Password</label>
                <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" placeholder="Enter password" />
              </div>
              {error && <div style={{ background: 'var(--danger-light)', color: 'var(--danger)', fontSize: 13, padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontWeight: 500 }}>{error}</div>}
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '9px 14px' }} disabled={checking || !username || !password}>
                {checking ? 'Signing in…' : 'Sign In'}
              </button>
              <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                Forgot your password? Contact your owner to reset it.
              </div>
            </form>
          )}

          {/* Attribution footer — kept inside the card so it reads as part
              of the sign-in chrome rather than floating free on the page. */}
          <div style={{ marginTop: 24, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5 }}>
            Developed by{' '}
            <a
              href="https://www.cognivaa.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontWeight: 600 }}
            >
              Cognivaa
            </a>
            . Brand of Pioneer Geoscience.
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ logout, user }}>
      {children}
    </AuthContext.Provider>
  );
}
